/**
 * useHiddenNav — the user's hidden top-level nav set, persisted to
 * `localStorage[storageKey]` (SCRUM-300 / MENU-4, epic SCRUM-296).
 *
 * The hidden-set is a USER PREFERENCE, not a desktop-only widget detail, so it
 * must be shared by every consumer of the same `storageKey`:
 *  - `SidebarV4` (desktop edit-menu + its visible-nav filter), and
 *  - `PmsShellV4` (always mounted; filters the compact `MobileNavDrawer`).
 *
 * **Why a module-level store, not `useState`.** Both consumers can be mounted
 * in the SAME tab at once (the shell is always mounted; the desktop sidebar
 * too). `localStorage`'s `storage` event does NOT fire in the document that
 * performed the write — so two independent `useState` hooks would drift ("hide
 * Accounting in the sidebar, it's back in the mobile drawer"). This hook is
 * therefore backed by a module-level, per-`storageKey` store consumed via
 * `useSyncExternalStore`: `setHide` writes `localStorage`, updates the store,
 * and notifies every subscriber → **synchronous same-tab parity** for all
 * consumers of that key. A `window` `storage` listener layers on **cross-tab**
 * sync. `getSnapshot` returns a cached `Set` reference (a new ref only when the
 * set actually changes), as `useSyncExternalStore` requires.
 *
 * **Empty-nav guard.** `dashboard` is non-hideable — the store never admits it
 * into the hidden set (`setHide('dashboard', true)` is a no-op, and a malformed
 * payload listing it is dropped), so the nav can never render empty.
 *
 * `shared/` is the leaf tier — both `entities` (`SidebarV4`) and `widgets`
 * (`PmsShellV4`) import it downward (ADR `frontend-0001`).
 */
import * as React from 'react';
import * as Sentry from '@sentry/react';

/** Top-level nav ids that can never be hidden (empty-nav guard). */
const NON_HIDEABLE: ReadonlySet<string> = new Set(['dashboard']);

const SENTRY_SURFACE = 'sidebar-v4-edit-menu';

/** Stable empty set for the SSR snapshot (a fresh ref each call would loop). */
const EMPTY: ReadonlySet<string> = Object.freeze(new Set<string>());

/**
 * Safe access to `window.localStorage`. In some browser privacy/security modes
 * the `window.localStorage` GETTER itself throws (not just getItem/setItem), so
 * a `typeof` check is insufficient — the access must be wrapped. Returns the
 * `Storage`, or `null` when it's unavailable or blocked, so callers degrade to
 * an empty set instead of crashing render (Simon review P2, SCRUM-300).
 */
function getLocalStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage ?? null;
  } catch {
    return null;
  }
}

/**
 * Parse the stored payload into a Set of hideable ids. Tolerant by design:
 * missing key → empty; malformed JSON → empty; non-array → empty; non-string
 * entries dropped; `NON_HIDEABLE` ids dropped (so a hand-edited/legacy payload
 * can never hide Dashboard). A read THROW (e.g. blocked storage) → Sentry +
 * empty (never throws into render).
 */
function readFromStorage(key: string): Set<string> {
  const ls = getLocalStorage();
  if (ls === null) return new Set();
  let raw: string | null;
  try {
    raw = ls.getItem(key);
  } catch (error) {
    Sentry.captureException(error, {
      tags: { surface: SENTRY_SURFACE, operation: 'localStorage.getItem' },
      extra: { key },
    });
    return new Set();
  }
  if (raw === null) return new Set();
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw) as unknown;
  } catch {
    return new Set(); // malformed JSON → tolerate
  }
  if (!Array.isArray(parsed)) return new Set(); // non-array → tolerate
  const out = new Set<string>();
  for (const v of parsed) {
    if (typeof v === 'string' && !NON_HIDEABLE.has(v)) out.add(v);
  }
  return out;
}

/**
 * Persist the set. A write THROW (quota exceeded, blocked storage) is reported
 * to Sentry but swallowed — the in-memory set stays authoritative so the UI
 * keeps working; persistence is best-effort (never throws into a click).
 */
function writeToStorage(key: string, hidden: ReadonlySet<string>): void {
  const ls = getLocalStorage();
  if (ls === null) return;
  try {
    ls.setItem(key, JSON.stringify([...hidden]));
  } catch (error) {
    Sentry.captureException(error, {
      tags: { surface: SENTRY_SURFACE, operation: 'localStorage.setItem' },
      extra: { key },
    });
  }
}

interface HiddenNavStore {
  readonly subscribe: (onChange: () => void) => () => void;
  readonly getSnapshot: () => ReadonlySet<string>;
  readonly setHide: (id: string, hide: boolean) => void;
}

function createStore(key: string): HiddenNavStore {
  let snapshot: ReadonlySet<string> = readFromStorage(key);
  const listeners = new Set<() => void>();
  const emit = (): void => {
    for (const listener of listeners) listener();
  };

  // Cross-tab sync — `storage` fires only in OTHER documents, so re-hydrate
  // from localStorage and notify our subscribers.
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (e: StorageEvent) => {
      // `e.key === null` is a cross-tab `localStorage.clear()` — rehydrate too,
      // else this tab keeps a stale hidden-set until reload. Ignore writes to
      // OTHER keys. (Simon review P3, SCRUM-300.)
      if (e.key !== null && e.key !== key) return;
      snapshot = readFromStorage(key);
      emit();
    });
  }

  return {
    subscribe(onChange) {
      listeners.add(onChange);
      return () => {
        listeners.delete(onChange);
      };
    },
    getSnapshot() {
      return snapshot;
    },
    setHide(id, hide) {
      // Empty-nav guard: Dashboard (etc.) can never be hidden.
      if (NON_HIDEABLE.has(id)) return;
      if (hide === snapshot.has(id)) return; // no change → no re-render/write
      const next = new Set(snapshot);
      if (hide) next.add(id);
      else next.delete(id);
      snapshot = next; // new ref → useSyncExternalStore re-renders subscribers
      writeToStorage(key, next);
      emit(); // synchronous SAME-TAB parity for every consumer of this key
    },
  };
}

/** One store per `storageKey`, shared across all consumers in this tab. */
const STORES = new Map<string, HiddenNavStore>();

function getStore(key: string): HiddenNavStore {
  let store = STORES.get(key);
  if (store === undefined) {
    store = createStore(key);
    STORES.set(key, store);
  }
  return store;
}

/**
 * TEST-ONLY: drop all module-level stores so a spec starts from a clean slate
 * (the stores are app-lifetime singletons in production). Pair with a
 * `localStorage.clear()` in the spec's `beforeEach`.
 */
export function __resetHiddenNavStoresForTests(): void {
  STORES.clear();
}

export interface UseHiddenNavResult {
  /** The current hidden top-level ids (never includes a `NON_HIDEABLE` id). */
  readonly hidden: ReadonlySet<string>;
  /** `true` when `id` is currently hidden. */
  readonly isHidden: (id: string) => boolean;
  /** Hide (`true`) or restore (`false`) `id`; persists + syncs all consumers. */
  readonly setHide: (id: string, hide: boolean) => void;
  /** `false` for permanent ids (Dashboard) that can never be hidden — lets the
      UI omit a dead add/remove control for them rather than render a no-op. */
  readonly isHideable: (id: string) => boolean;
}

export function useHiddenNav(storageKey: string): UseHiddenNavResult {
  const store = getStore(storageKey);
  const hidden = React.useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    () => EMPTY, // server snapshot — SSR-safe, stable ref
  );
  const isHidden = React.useCallback(
    (id: string): boolean => hidden.has(id),
    [hidden],
  );
  const setHide = React.useCallback(
    (id: string, hide: boolean): void => store.setHide(id, hide),
    [store],
  );
  const isHideable = React.useCallback(
    (id: string): boolean => !NON_HIDEABLE.has(id),
    [],
  );
  return { hidden, isHidden, setHide, isHideable };
}
